import React, { useMemo, useState, useEffect } from 'react';
import ApiClient, { ApiKey } from '../api/client';
import ApiKeyDialog from './ApiKeyDialog';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { usePermissions } from '../context/PermissionContext';
import { DataTableShell, PageHeader, SortableTableHeader } from './ui';
import './ApiKeyManager.css';

interface ApiKeyManagerProps {
  apiClient: ApiClient;
}

interface ApiKeyWithSecret extends ApiKey {
  key?: string;
}

type ApiKeySortKey = 'name' | 'role' | 'active' | 'createdAt' | 'expiresAt';
type SortDirection = 'asc' | 'desc';

const ApiKeyManager: React.FC<ApiKeyManagerProps> = ({ apiClient }) => {
  const { hasPermission } = usePermissions();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState<number | null>(null);
  const [newKeyData, setNewKeyData] = useState<{
    key: string;
    id: number;
  } | null>(null);
  const [deactivatingKeyId, setDeactivatingKeyId] = useState<number | null>(null);
  const [deletingKeyId, setDeletingKeyId] = useState<number | null>(null);
  const [rotatingKeyId, setRotatingKeyId] = useState<number | null>(null);
  const [sort, setSort] = useState<{ key: ApiKeySortKey; direction: SortDirection }>({
    key: 'createdAt',
    direction: 'desc',
  });

  const canManage = hasPermission('api_keys.manage');

  const sortedApiKeys = useMemo(() => {
    return [...apiKeys].sort((a, b) => {
      const aValue = getApiKeySortValue(a, sort.key);
      const bValue = getApiKeySortValue(b, sort.key);
      const result = typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });
      return sort.direction === 'asc' ? result : -result;
    });
  }, [apiKeys, sort]);

  const updateSort = (key: ApiKeySortKey) => {
    setSort(current => current.key === key
      ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: key === 'createdAt' || key === 'expiresAt' ? 'desc' : 'asc' });
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.listApiKeys();
      setApiKeys(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async (data: {
    name: string;
    description?: string;
    role?: 'user' | 'admin';
    expiresIn?: number;
    permissions?: string[];
  }) => {
    try {
      const newKey = await apiClient.createApiKey(data);
      setApiKeys([...apiKeys, newKey]);
      setNewKeyData({ key: newKey.key, id: newKey.id });
      setShowDialog(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key');
    }
  };

  const handleDeactivateKey = (id: number) => {
    setDeactivatingKeyId(id);
  };

  const handleConfirmDeactivateKey = async () => {
    if (deactivatingKeyId === null) return;

    try {
      await apiClient.deactivateApiKey(deactivatingKeyId);
      setApiKeys(
        apiKeys.map((k) =>
          k.id === deactivatingKeyId ? { ...k, active: false } : k
        )
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to deactivate key');
    } finally {
      setDeactivatingKeyId(null);
    }
  };

  const handleDeleteKey = (id: number) => {
    setDeletingKeyId(id);
  };

  const handleConfirmDeleteKey = async () => {
    if (deletingKeyId === null) return;

    try {
      await apiClient.deleteApiKey(deletingKeyId);
      setApiKeys(apiKeys.filter((k) => k.id !== deletingKeyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete key');
    } finally {
      setDeletingKeyId(null);
    }
  };

  const handleCopyKey = (key: string, id: number) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(id);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  const handleRotateKey = (id: number) => {
    setRotatingKeyId(id);
  };

  const handleConfirmRotateKey = async () => {
    if (rotatingKeyId === null) return;

    try {
      const rotatedKey = await apiClient.rotateApiKey(rotatingKeyId);
      setApiKeys(
        apiKeys.map((k) =>
          k.id === rotatingKeyId ? { ...rotatedKey } : k
        )
      );
      setNewKeyData({ key: rotatedKey.key, id: rotatedKey.id });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate key');
    } finally {
      setRotatingKeyId(null);
    }
  };

  return (
    <div className="manager-page api-keys-page">
      {error && (
        <div className="alert alert-error">
          <span>Error</span>
          <span>{error}</span>
        </div>
      )}

      {newKeyData && (
        <div className="alert alert-success new-key-alert">
          <span>Created</span>
          <div>
            <strong>API Key Created!</strong>
            <p style={{ margin: '4px 0 0 0' }}>
              Copy this key now. It won't be shown again:
            </p>
            <code className="new-key-value">{newKeyData.key}</code>
            <button
              className="btn btn-sm btn-secondary"
              style={{ marginLeft: '8px' }}
              onClick={() => {
                navigator.clipboard.writeText(newKeyData.key);
              }}
            >
              Copy
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => setNewKeyData(null)}
              style={{ marginLeft: '4px' }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <PageHeader
        title="API Keys"
        actions={canManage && (
          <button className="btn btn-primary" onClick={() => setShowDialog(true)}>
            + Create API Key
          </button>
        )}
      />

      <div className="api-keys-shell">
        {loading ? (
          <div className="empty-state">
            <div className="loading-spinner"></div>
            <p>Loading API keys...</p>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="empty-state">
            <h3>No API keys yet</h3>
            <p>Create an API key for programmatic access</p>
          </div>
        ) : (
          <DataTableShell className="api-keys-table">
            <table className="data-table api-keys-data-table">
              <thead>
                <tr>
                  <SortableTableHeader active={sort.key === 'name'} direction={sort.direction} onClick={() => updateSort('name')}>Name</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'role'} direction={sort.direction} onClick={() => updateSort('role')}>Role</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'active'} direction={sort.direction} onClick={() => updateSort('active')}>Status</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'createdAt'} direction={sort.direction} onClick={() => updateSort('createdAt')}>Created</SortableTableHeader>
                  <SortableTableHeader active={sort.key === 'expiresAt'} direction={sort.direction} onClick={() => updateSort('expiresAt')}>Expires</SortableTableHeader>
                  <th className="actions-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedApiKeys.map((key) => (
                  <tr key={key.id} className={!key.active ? 'inactive' : ''}>
                    <td className="key-name">
                      <div className="name-label">{key.name}</div>
                      {key.description && (
                        <div className="name-desc">{key.description}</div>
                      )}
                    </td>
                    <td>
                      <span className={`role-badge ${key.role}`}>
                        {key.role === 'admin' ? 'Admin' : 'User'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${key.active ? 'status-approved' : 'status-badge-neutral'}`}>
                        {key.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="date">
                      {new Date(key.createdAt).toLocaleDateString()}
                    </td>
                    <td className="date">
                      {key.expiresAt
                        ? new Date(key.expiresAt).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="actions actions-cell">
                      {canManage && (
                        <>
                          {key.active && (
                            <>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleRotateKey(key.id)}
                                title="Rotate this key (generates new secret)"
                              >
                                Rotate
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleDeactivateKey(key.id)}
                                title="Deactivate this key"
                              >
                                Deactivate
                              </button>
                            </>
                          )}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteKey(key.id)}
                            title="Delete this key permanently"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableShell>
        )}
      </div>

      {showDialog && (
        <ApiKeyDialog
          apiClient={apiClient}
          onClose={() => setShowDialog(false)}
          onSubmit={handleCreateApiKey}
        />
      )}

      {deactivatingKeyId !== null && (
        <ConfirmDeleteModal
          title="Deactivate API Key"
          message="Deactivate this API key? It will no longer be usable for authentication."
          confirmButtonLabel="Deactivate"
          onConfirm={handleConfirmDeactivateKey}
          onCancel={() => setDeactivatingKeyId(null)}
        />
      )}

      {deletingKeyId !== null && (
        <ConfirmDeleteModal
          title="Delete API Key"
          message="Delete this API key permanently? This action cannot be undone."
          confirmButtonLabel="Delete"
          onConfirm={handleConfirmDeleteKey}
          onCancel={() => setDeletingKeyId(null)}
        />
      )}

      {rotatingKeyId !== null && (
        <ConfirmDeleteModal
          title="Rotate API Key"
          message="Generate a new secret for this API key? The old secret will stop working immediately."
          confirmButtonLabel="Rotate"
          onConfirm={handleConfirmRotateKey}
          onCancel={() => setRotatingKeyId(null)}
        />
      )}
    </div>
  );
};

function getApiKeySortValue(key: ApiKey, sortKey: ApiKeySortKey): string | number {
  if (sortKey === 'active') return key.active ? 1 : 0;
  if (sortKey === 'createdAt') return new Date(key.createdAt).getTime();
  if (sortKey === 'expiresAt') return key.expiresAt ? new Date(key.expiresAt).getTime() : Number.MAX_SAFE_INTEGER;
  return key[sortKey] || '';
}

export default ApiKeyManager;
